// kontaktAnlageTypen.ts — der dritte Einstieg im „Neu"-Menü neben Firma und Privat.
//
// Ein Verein ist datenmodellhaft eine Firma, in der Anlage aber ein eigener
// Einstieg: Der Anwender sucht „Verein", nicht „Firma mit Rechtsform e.V.".
//
// Bis 19.08.2026 machte diese Datei aus dem Rechtsform-Katalog VIER Menüpunkte
// (Genossenschaft, Verein, Stiftung, Gemeinschaft). Der PO hat daraus EINEN
// Sammeleintrag gemacht: Die Maske ist für alle vier dieselbe, und welche
// Körperschaft es genau ist, entscheidet die Rechtsform IN der Maske — die
// Beschriftungen folgen ihr live (utils/koerperschaftBegriff.ts).
//
// Vorbelegt wird der eingetragene Verein; ohne ihn im Katalog entsteht kein
// Menüpunkt — eine geratene Vorbelegung wäre schlechter als keine.
//
// ⚠️ In die URL geht der stabile `RechtsformKey`, NIE die Katalog-Id: DEV und
// PROD vergeben für dieselbe Rechtsform verschiedene Ids (PROD 8 = „GmbH &
// Co. KG", DEV 8 = „AG"). Aufgelöst wird beidseitig über `findeKatalogEintrag`.

import * as React from "react";
import {
  RECHTSFORM_KATALOG_ALIASE,
  findeKatalogEintrag,
  type RechtsformKey,
} from "@kmvs/forms-core";
import type { LegalFormOption } from "./legalFormOptions";
import { loadLegalFormOptionsCached } from "./unternehmensBegriff";

/** Ein zusätzlicher Eintrag im „Neu"-Menü. */
export type KontaktAnlageTyp = {
  /** Beschriftung des Menüpunkts. */
  label: string;
  /** Stabiler Schlüssel der vorbelegten Rechtsform — geht als Query-Param mit. */
  rechtsformKey: RechtsformKey;
};

/**
 * Der Eintrag heißt bewusst nicht „Verein": Stiftung, Genossenschaft und
 * Gemeinschaft laufen über denselben Menüpunkt, und wer sie sucht, muss sie im
 * Menü wiederfinden.
 */
export const KOERPERSCHAFT_ANLAGE_LABEL = "Verein, Stiftung, …";

/** Vorbelegte Rechtsform des Sammeleintrags — der häufigste dieser Fälle. */
const VORBELEGUNG: RechtsformKey = "EV";

/**
 * Der Menüeintrag aus einem bereits geladenen Katalog. Kennt der Katalog keinen
 * eingetragenen Verein, gibt es nichts vorzubelegen und damit auch keinen
 * Einstieg — das Menü zeigt dann nur Firma und Privat.
 */
export const kontaktAnlageTypAus = (options: LegalFormOption[]): KontaktAnlageTyp | null =>
  findeKatalogEintrag(VORBELEGUNG, options)
    ? { label: KOERPERSCHAFT_ANLAGE_LABEL, rechtsformKey: VORBELEGUNG }
    : null;

/**
 * Der Menüeintrag für die Anlage. Der Katalog kommt aus dem Sitzungs-Cache — ein
 * Fehlschlag lässt ihn weg, das Menü zeigt dann nur Firma und Privat.
 */
export const useKontaktAnlageTyp = (): KontaktAnlageTyp | null => {
  const [typ, setTyp] = React.useState<KontaktAnlageTyp | null>(null);

  React.useEffect(() => {
    let aktiv = true;
    loadLegalFormOptionsCached()
      .then((options) => {
        if (aktiv) setTyp(kontaktAnlageTypAus(options));
      })
      .catch(() => {
        /* ohne Katalog bleibt es bei Firma und Privat */
      });
    return () => {
      aktiv = false;
    };
  }, []);

  return typ;
};

/** Ziel-Route eines Anlage-Typs: Firmenmaske mit vorbelegter Rechtsform. */
export const kontaktAnlagePfad = (typ: KontaktAnlageTyp): string =>
  `/contacts/new/company?rechtsform=${encodeURIComponent(typ.rechtsformKey)}`;

/**
 * Query-Param → bekannter Schlüssel, OHNE Katalog. Die Alias-Tabelle ist ein
 * statischer Import, deshalb steht schon beim ersten Rendern fest, ob eine
 * Vorbelegung kommt — die Anlage-Maske muss ihre Rechtsform-Autoerkennung
 * sperren, bevor irgendein Fetch zurück ist. Unbekannter Schlüssel ⇒ `null`,
 * damit ein vertippter Query-Param die Autoerkennung nicht grundlos lahmlegt.
 *
 * Angeboten wird nur noch `EV`; erkannt werden muss weiterhin JEDER Schlüssel:
 * `?rechtsform=STIFTUNG` steht in Lesezeichen und offenen Tabs aus der Zeit der
 * vier Menüpunkte.
 */
export const rechtsformKeyAusParam = (param: string | null | undefined): RechtsformKey | null => {
  const key = (param ?? "").trim().toUpperCase();
  return key && key in RECHTSFORM_KATALOG_ALIASE ? (key as RechtsformKey) : null;
};

/** Query-Param → Katalogeintrag. Unbekannter Schlüssel ⇒ keine Vorbelegung. */
export const rechtsformAusParam = (
  param: string | null | undefined,
  options: LegalFormOption[],
): LegalFormOption | null => {
  const key = rechtsformKeyAusParam(param);
  return key ? findeKatalogEintrag(key, options) : null;
};
